import { EventEmitter } from 'events';
import { AISignal } from './ai-signals-api';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error' | 'reconnecting';

export interface WebSocketSignalMessage {
  type: 'signal';
  data: AISignal;
  timestamp: string;
}

export interface WebSocketErrorMessage {
  type: 'error';
  message: string;
  timestamp: string;
}

export type WebSocketMessage = WebSocketSignalMessage | WebSocketErrorMessage;

export class AISignalsWebSocket extends EventEmitter {
  private static instance: AISignalsWebSocket;
  private ws: WebSocket | null = null;
  private url: string;
  private connectionStatus: ConnectionStatus = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start at 1s
  private maxReconnectDelay = 30000; // Max 30s
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private heartbeatIntervalMs = 30000; // 30s

  private constructor() {
    super();
    this.setMaxListeners(50); // Allow more listeners for signal callbacks
    this.url = this.getProductionWsUrl();
  }

  private getProductionWsUrl(): string {
    // Try production URL first
    const primaryUrl = import.meta.env.VITE_AI_BACKEND_WS_URL;
    const fallbackUrl = import.meta.env.VITE_AI_WS_FALLBACK_URL;
    
    // In production, try primary first, then fallback
    if (import.meta.env.PROD && primaryUrl) {
      console.log('[WebSocket] Using production primary URL:', primaryUrl);
      return primaryUrl + '/ws/signals';
    }
    
    if (fallbackUrl) {
      console.log('[WebSocket] Using fallback URL:', fallbackUrl);
      return fallbackUrl + '/ws/signals';
    }
    
    // Development fallback
    console.log('[WebSocket] Using development fallback URL');
    return 'ws://localhost:8000/ws/signals';
  }

  public static getInstance(): AISignalsWebSocket {
    if (!AISignalsWebSocket.instance) {
      AISignalsWebSocket.instance = new AISignalsWebSocket();
    }
    return AISignalsWebSocket.instance;
  }

  public connect(fallbackTried = false): void {
    if (this.connectionStatus === 'connecting' || this.connectionStatus === 'connected') {
      return;
    }

    this.connectionStatus = 'connecting';
    this.emit('connectionChange', this.connectionStatus);

    const currentUrl = fallbackTried ? this.getFallbackWsUrl() : this.url;
    
    try {
      console.log(`[WebSocket] Connecting to: ${currentUrl}`);
      this.ws = new WebSocket(currentUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected to AI signals');
        this.connectionStatus = 'connected';
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.emit('connectionChange', this.connectionStatus);
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
          this.emit('error', new Error('Invalid message format'));
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.stopHeartbeat();
        this.handleDisconnect(fallbackTried);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.connectionStatus = 'error';
        this.emit('connectionChange', this.connectionStatus);
        
        // Try fallback if primary fails and we haven't tried it yet
        if (!fallbackTried) {
          console.log('[WebSocket] Primary URL failed, trying fallback...');
          setTimeout(() => this.connect(true), 1000);
          return;
        }
        
        this.emit('error', new Error('WebSocket connection error'));
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.connectionStatus = 'error';
      this.emit('connectionChange', this.connectionStatus);
      this.emit('error', error as Error);
    }
  }

  private getFallbackWsUrl(): string {
    const fallbackUrl = import.meta.env.VITE_AI_WS_FALLBACK_URL;
    if (fallbackUrl) {
      console.log('[WebSocket] Using fallback URL:', fallbackUrl);
      return fallbackUrl + '/ws/signals';
    }
    return 'ws://localhost:8000/ws/signals';
  }

  public disconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.connectionStatus = 'disconnected';
    this.emit('connectionChange', this.connectionStatus);
  }

  public onSignal(callback: (signal: AISignal) => void): void {
    this.on('signal', callback);
  }

  public onConnectionChange(callback: (status: ConnectionStatus) => void): void {
    this.on('connectionChange', callback);
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'signal':
        if (this.validateSignal(message.data)) {
          this.emit('signal', message.data);
        } else {
          console.warn('Invalid signal structure received:', message.data);
          this.emit('error', new Error('Invalid signal structure'));
        }
        break;
      case 'error':
        console.error('WebSocket error message:', message.message);
        this.emit('error', new Error(message.message));
        break;
      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  private validateSignal(signal: any): signal is AISignal {
    return (
      typeof signal === 'object' &&
      signal !== null &&
      typeof signal.id === 'string' &&
      typeof signal.symbol === 'string' &&
      ['BUY', 'SELL', 'HOLD'].includes(signal.signal_type) &&
      typeof signal.confidence_score === 'number' &&
      ['HIGH', 'MEDIUM', 'LOW'].includes(signal.confidence_level) &&
      typeof signal.current_price === 'number' &&
      typeof signal.risk_score === 'number' &&
      ['IMMEDIATE', 'SHORT_TERM', 'MEDIUM_TERM', 'LONG_TERM'].includes(signal.time_horizon) &&
      typeof signal.model_name === 'string' &&
      typeof signal.model_version === 'string' &&
      typeof signal.reasoning === 'string' &&
      typeof signal.status === 'string' &&
      typeof signal.created_at === 'string'
    );
  }

  private handleDisconnect(fallbackTried = false): void {
    this.connectionStatus = 'disconnected';
    this.emit('connectionChange', this.connectionStatus);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.attemptReconnect(fallbackTried);
    } else {
      console.error('Max reconnection attempts reached');
      this.emit('error', new Error('Max reconnection attempts reached'));
    }
  }

  private attemptReconnect(fallbackTried = false): void {
    this.reconnectAttempts++;
    this.connectionStatus = 'reconnecting';
    this.emit('connectionChange', this.connectionStatus);

    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectDelay}ms`);

    setTimeout(() => {
      this.connect(fallbackTried);
    }, this.reconnectDelay);

    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
      }
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  public isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }
}